import { PrimaryButton, TextButton } from '../components/Button';
import { LiveRegion, Screen } from '../components/Screen';
import { StatusChip, StatusRing } from '../components/Status';
import { copy } from '../copy';
import { shortParcelLabel } from '../lib/parcel';
import { loadCounts, visibleStops } from '../state/route';
import { useAppStore } from '../state/store';

/** D1–D4 — Ophalen / laadlijst. De lijst blijft in vaste volgorde: de bezorger kijkt mee. */
export function LoadScreen() {
  const route = useAppStore((s) => s.route);
  const navigate = useAppStore((s) => s.navigate);
  const openSheet = useAppStore((s) => s.openSheet);
  const setScanPurpose = useAppStore((s) => s.setScanPurpose);
  const setNotTakenParcel = useAppStore((s) => s.setNotTakenParcel);
  const recordAction = useAppStore((s) => s.recordAction);

  if (!route) return null;

  const counts = loadCounts(route);
  const counterText =
    counts.notTaken > 0
      ? copy.load.counterWithMissing(counts.loaded, counts.total, counts.notTaken)
      : copy.load.counter(counts.loaded, counts.total);

  const rows = visibleStops(route).flatMap((stop) =>
    stop.parcels.map((parcel) => ({ stop, parcel })),
  );

  const startRide = () => {
    void recordAction({ type: 'route.start', request: {} }).then(() => navigate('ride'));
  };

  const openScanner = () => {
    setScanPurpose('load');
    navigate('scanner');
  };

  const openManual = () => {
    setScanPurpose('load');
    navigate('manual');
  };

  return (
    <Screen
      name="load"
      footDivided
      foot={
        counts.complete ? (
          <PrimaryButton variant="positive" onClick={startRide}>
            {copy.load.startRide}
          </PrimaryButton>
        ) : (
          <>
            <PrimaryButton onClick={openScanner}>{copy.load.scan}</PrimaryButton>
            <TextButton center onClick={openManual}>
              {copy.load.manual}
            </TextButton>
          </>
        )
      }
    >
      <div className="pad-x" style={{ paddingTop: 16 }}>
        <h1 className="t-serif t-titel">{copy.load.title}</h1>
        <p style={{ fontSize: 22, fontWeight: 600, marginTop: 6, marginBottom: 0 }}>
          {counterText}
        </p>
        <LiveRegion>{counterText}</LiveRegion>
      </div>

      {rows.length === 0 ? (
        <p className="o-75 pad-x" style={{ fontSize: 20, paddingTop: 24 }}>
          {copy.load.empty}
        </p>
      ) : (
        <div className="stack--sm stack pad-x" style={{ paddingTop: 16, paddingBottom: 8 }}>
          {rows.map(({ stop, parcel }) => {
            const city = stop.postalCodeCity.split(' ').slice(2).join(' ') || stop.postalCodeCity;
            const state = parcel.loaded ? 'delivered' : parcel.notTaken ? 'notTaken' : 'open';
            const ariaLabel = parcel.loaded
              ? 'geladen'
              : parcel.notTaken
                ? copy.status.notTaken
                : 'nog te laden';
            return (
              <div
                key={parcel.parcelNumber}
                className={`listrow${parcel.loaded ? ' card--success' : ''}${
                  parcel.notTaken ? ' card--warning' : ''
                }`}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="listrow__mono">{shortParcelLabel(parcel.parcelNumber)}</div>
                  <div className="listrow__sub">
                    {copy.load.destination(stop.partnerName, city)}
                  </div>
                  <div className="row" style={{ gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                    {parcel.earlyPickup ? (
                      <StatusChip tone="neutral">{copy.status.earlyPickup}</StatusChip>
                    ) : null}
                    {parcel.notTaken ? (
                      <StatusChip tone="waiting">{copy.status.notTaken}</StatusChip>
                    ) : null}
                    {!parcel.loaded && !parcel.notTaken ? (
                      <TextButton
                        variant="muted"
                        onClick={() => {
                          setNotTakenParcel(parcel.parcelNumber);
                          openSheet('notTaken');
                        }}
                      >
                        {copy.load.rowAction}
                      </TextButton>
                    ) : null}
                  </div>
                </div>
                <StatusRing state={state} size={28} ariaLabel={ariaLabel} />
              </div>
            );
          })}
        </div>
      )}
    </Screen>
  );
}
